import React, { useRef, useEffect, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { notificationService } from '../../services/notificationService';
import type { Notification } from '../../types';

interface NotificationDropdownProps {
  isOpen: boolean;
  onToggle: () => void;
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ 
  isOpen, 
  onToggle 
}) => {
  const { user } = useAuth();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [notifications, setNotifications] = useState<Notification[]>(() => notificationService.getNotifications());
  const [seenNotificationIds, setSeenNotificationIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const unsubscribe = notificationService.subscribe(setNotifications);
    void notificationService.loadFromDatabase(user?.organizationId ?? undefined, user?.id, 1, 50);
    return unsubscribe;
  }, [user?.id, user?.organizationId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onToggle();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onToggle]);

  const unreadNotifications = notifications.filter(n => !n.read);
  const badgeNotifications = unreadNotifications.filter(n => !seenNotificationIds.has(n.id));
  const badgeCount = badgeNotifications.length;

  useEffect(() => {
    if (!isOpen || badgeNotifications.length === 0) return;
    setSeenNotificationIds((currentIds) => {
      const nextIds = new Set(currentIds);
      badgeNotifications.forEach((notification) => nextIds.add(notification.id));
      return nextIds;
    });
  }, [badgeNotifications, isOpen]);
  
  // Get the most critical notification color for the bell icon
  const getBellColor = () => {
    if (badgeNotifications.length === 0) return 'text-muted-foreground';
    
    // Priority order: Critical > Performance > Maintenance > Asset > Rules > Sensor > System
    const hasCritical = badgeNotifications.some(n => n.category === 'SECURITY_ALERT' || n.category === 'SAFETY_ALERT');
    const hasPerformance = badgeNotifications.some(n => n.category === 'PERFORMANCE_ALERT' || n.category === 'DEVICE_OFFLINE');
    const hasMaintenance = badgeNotifications.some(n => n.category.includes('MAINTENANCE'));
    const hasAsset = badgeNotifications.some(n => n.category.includes('DEVICE'));
    const hasRules = badgeNotifications.some(n => n.category.includes('RULE'));
    const hasSensor = badgeNotifications.some(n => n.category === 'TEMPERATURE_ALERT' || n.category === 'BATTERY_LOW');
    
    if (hasCritical) return 'text-red-600';
    if (hasPerformance) return 'text-orange-600';
    if (hasMaintenance) return 'text-yellow-600';
    if (hasAsset) return 'text-green-600';
    if (hasRules) return 'text-purple-600';
    if (hasSensor) return 'text-amber-600';
    return 'text-blue-600';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={onToggle}
        aria-label="Notifications"
        title="Notifications"
        className="relative p-2 hover:bg-muted rounded-md transition-colors"
      >
        <Bell className={`w-5 h-5 ${getBellColor()}`} />
        {badgeCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
            {badgeCount > 9 ? '9+' : badgeCount}
          </span>
        )}
      </button>

      {/* Clean Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-16 w-80 bg-popover text-popover-foreground rounded-lg shadow-lg border border-border z-50 max-h-96 overflow-hidden">
          {/* Simple Header */}
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-popover-foreground">Notifications</h3>
                <p className="text-sm text-muted-foreground">
                  {unreadNotifications.length} unread
                </p>
              </div>
              <button
                onClick={onToggle}
                className="p-1 hover:bg-muted rounded-md transition-colors"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* Notifications List - Simple titles only */}
          <div className="max-h-64 overflow-y-auto">
            {unreadNotifications.length === 0 ? (
              <div className="p-6 text-center">
                <Bell className="w-8 h-8 text-muted-foreground/70 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No unread notifications</p>
                <p className="text-xs text-muted-foreground/70 mt-1">All caught up!</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {unreadNotifications.map((notification) => (
                  <div 
                    key={notification.id} 
                    className="px-4 py-3"
                  >
                    <p className="text-sm text-popover-foreground font-medium truncate">
                      {notification.title}
                    </p>
                    {notification.message && (
                      <p className="mt-1 line-clamp-2 text-xs leading-5 text-muted-foreground">
                        {notification.message}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
